import { useEffect } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

export function TablePagination({
  page,
  pageSize,
  total,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // If the underlying total shrinks (e.g. items resolved/filtered) while the
  // user is on a now-out-of-range page, snap back to the last valid page.
  useEffect(() => {
    if (total > 0 && page > totalPages) onPageChange(totalPages);
  }, [page, totalPages, total, onPageChange]);

  if (total === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
      <p className="text-xs text-gray-500">
        Showing <span className="font-medium text-[#0B132B]">{from}</span>–
        <span className="font-medium text-[#0B132B]">{to}</span> of{" "}
        <span className="font-medium text-[#0B132B]">{total}</span>
      </p>
      <Pagination className="mx-0 w-auto justify-end">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={(e) => {
                e.preventDefault();
                if (canPrev) onPageChange(page - 1);
              }}
              aria-disabled={!canPrev}
              className={
                canPrev ? "cursor-pointer" : "pointer-events-none opacity-50"
              }
            />
          </PaginationItem>
          <PaginationItem>
            <span className="px-3 text-sm text-[#0B132B]">
              Page {page} of {totalPages}
            </span>
          </PaginationItem>
          <PaginationItem>
            <PaginationNext
              onClick={(e) => {
                e.preventDefault();
                if (canNext) onPageChange(page + 1);
              }}
              aria-disabled={!canNext}
              className={
                canNext ? "cursor-pointer" : "pointer-events-none opacity-50"
              }
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}
